import type { Optional } from '@festina-life/core'
import {
  Disclosure,
  DisclosureButton,
  DisclosurePanel,
} from '@headlessui/react'
import { ChevronRightIcon, FolderIcon } from '@heroicons/react/24/outline'
import cx from 'classnames'
import { Activity, useCallback, useEffect, useMemo, useRef } from 'react'

import { useTranslate } from '../../hooks/useTranslate'
import { useTreeContext } from './treeContext'
import type { TreeLeafData, TreeNodeData } from './types'

const DEFAULT_STEP_INDENTATION = 16

type Props<Leaf extends TreeLeafData> = {
  node: TreeNodeData<Leaf>
  /** Current number of indentation steps for the table. */
  indentationStep?: number
  /** Size in Px of each indentation step.*/
  stepSizePx?: number
}

export const TreeNode = <Leaf extends TreeLeafData>({
  node,
  indentationStep = 0,
  stepSizePx = DEFAULT_STEP_INDENTATION,
}: Props<Leaf>) => {
  const translate = useTranslate()
  const nodePath = node.path.join('.')
  const {
    renderLeaf,
    renderTags,
    renderNode,
    expandedPaths,
    onTogglePath,
    containerRef,
    nodeClassName,
    leafClassName,
    onSelectNode,
    onSelectLeaf,
    renderNodeActions,
    renderLeafActions,
    shouldSortNodes,
    isVisible,
  } = useTreeContext()
  const levelIndentationPx = indentationStep * stepSizePx
  const isOpen = expandedPaths.includes(node.path.join('.'))
  const activeElementObserverRef = useRef<IntersectionObserver>(undefined)
  const activeLeafElementRef = useRef<Optional<HTMLDivElement>>(undefined)
  const sortedNodes = useMemo(
    () =>
      shouldSortNodes
        ? [...node.nodes].sort((nodeA, nodeB) =>
            nodeA.name.localeCompare(nodeB.name, undefined, {
              sensitivity: 'base',
            }),
          )
        : node.nodes,
    [node.nodes, shouldSortNodes],
  )

  const sortedLeaves = useMemo(
    () =>
      [...node.leaves].sort((templateA, templateB) =>
        templateA.id.localeCompare(templateB.id, undefined, {
          sensitivity: 'base',
        }),
      ),
    [node.leaves],
  )

  const handleMountedActiveElement = useCallback(
    (leafRef?: Optional<HTMLDivElement>) => {
      if (leafRef) {
        activeLeafElementRef.current = leafRef
        const options = {
          root: containerRef,
          rootMargin: '0px',
          threshold: 1,
        }

        if (activeElementObserverRef.current) {
          activeElementObserverRef.current?.disconnect()
        }

        activeElementObserverRef.current = new IntersectionObserver(
          (entries, observer) => {
            if (entries.length) {
              if (!entries[0].isIntersecting) {
                leafRef.scrollIntoView({
                  behavior: 'instant',
                  block: 'center',
                })
              } else {
                observer.unobserve(leafRef)
              }
            }
          },
          options,
        )
        activeElementObserverRef.current.observe(leafRef)
      }
    },
    [containerRef],
  )

  useEffect(function destroyInteractionObserver() {
    return () => {
      activeElementObserverRef.current?.disconnect()
    }
  }, [])

  useEffect(() => {
    if (isVisible && activeLeafElementRef.current) {
      activeLeafElementRef.current.scrollIntoView({
        behavior: 'instant',
        block: 'center',
      })
    }
  }, [isVisible])

  return (
    <Disclosure>
      <div
        className={cx(
          'group relative grow flex w-full items-center rounded-md select-none',
          typeof nodeClassName === 'function'
            ? nodeClassName(node)
            : nodeClassName,
          {
            'bg-primary-200 dark:bg-primary-700': node.isActive,
            'hover:bg-slate-100 dark:hover:bg-slate-700': !node.isActive,
          },
        )}
        style={{ paddingLeft: `${levelIndentationPx}px` }}
      >
        <DisclosureButton
          as="div"
          role="button"
          className="flex gap-x-1 items-center w-full"
          onClick={() => onSelectNode?.(node)}
          onDoubleClick={() => onTogglePath(nodePath)}
        >
          <ChevronRightIcon
            className={cx('size-3 transition-transform cursor-pointer', {
              'rotate-90': isOpen,
            })}
            role="button"
            onClick={(event) => {
              event.stopPropagation()
              onTogglePath(nodePath)
            }}
          />
          {renderNode?.(node) ?? (
            <>
              <FolderIcon className="size-4" />
              {node.name || `(${translate('empty')})`}
            </>
          )}
          {!!node.tags.length && renderTags?.(node.tags)}
        </DisclosureButton>
        <div className="shrink flex items-center pr-1">
          {renderNodeActions?.(node)}
        </div>
      </div>
      <Activity mode={isOpen ? 'visible' : 'hidden'}>
        <DisclosurePanel static className="size-full">
          {sortedNodes.map((child) => (
            <TreeNode
              key={child.name}
              node={child}
              indentationStep={indentationStep + 1}
            />
          ))}
          {sortedLeaves.map((leaf, index) => (
            <div
              className={cx(
                'flex w-full items-center rounded-md group',
                typeof leafClassName === 'function'
                  ? leafClassName(leaf)
                  : leafClassName,
                {
                  'bg-primary-200 dark:bg-primary-700': leaf.isActive,
                  'hover:bg-slate-100 dark:hover:bg-slate-700': !leaf.isActive,
                },
              )}
              // oxlint-disable-next-line react/no-array-index-key
              key={`${leaf.id}-${leaf.isActive}-${index}`}
              ref={leaf.isActive ? handleMountedActiveElement : undefined}
            >
              <div
                className="grow flex items-center"
                role="button"
                onClick={(event) => {
                  event.stopPropagation()
                  event.preventDefault()
                  onSelectLeaf?.(leaf)
                }}
                style={{
                  paddingLeft: `calc(${levelIndentationPx + stepSizePx}px + (var(--spacing) * 4))`, // indentation + spacing of the chevron (size-3) and the disclosure button gap (1 space)
                }}
              >
                {renderLeaf(leaf)}
              </div>
              <div className="shrink flex items-center pr-1">
                {renderLeafActions?.(leaf)}
              </div>
            </div>
          ))}
        </DisclosurePanel>
      </Activity>
    </Disclosure>
  )
}
